import { auCentime, compteDansLeCa, soldeDu, ttcDocument } from './vat';

/**
 * Les avoirs émis sur une facture : ce qu'ils rendent au client se retranche
 * de ce qu'il doit encore.
 *
 * @param {object} f facture créditée
 * @param {object[]} [factures] toutes les factures, avoirs compris
 * @returns {object[]} les avoirs émis (ni brouillon, ni annulés) sur cette facture
 */
export function avoirsSur(f, factures) {
  if (!f || f.id == null) return [];
  return (factures || []).filter(
    (a) =>
      a.type === 'avoir' &&
      compteDansLeCa(a) &&
      a.avoirDe != null &&
      String(a.avoirDe) === String(f.id),
  );
}

/**
 * Le solde d'une facture, une fois ses avoirs retranchés.
 *
 * @param {object} f facture
 * @param {object[]} [factures] toutes les factures, pour trouver ses avoirs
 * @returns {number} le TTC que le client doit encore
 */
export function soldeApresAvoirs(f, factures) {
  const credit = avoirsSur(f, factures).reduce((s, a) => s + ttcDocument(a), 0);
  return Math.max(0, auCentime(soldeDu(f) - credit));
}

/**
 * Une facture soldée par un avoir : plus rien à encaisser, plus de rappel.
 *
 * @param {object} f facture
 * @param {object[]} [factures] toutes les factures
 * @returns {boolean} vrai si ses avoirs couvrent tout son solde
 */
export function regleeParAvoir(f, factures) {
  return avoirsSur(f, factures).length > 0 && soldeApresAvoirs(f, factures) <= 0.005;
}

/**
 * Une facture dont le client doit encore de l'argent : envoyée ou payée en
 * partie. Un avoir ne se paie pas, et une facture soldée par son avoir ne
 * doit plus rien.
 *
 * <p>La facture d'acompte part chez le client à l'acceptation de l'offre :
 * elle compte dans le chiffre d'affaires, la TVA, les débiteurs du bilan et
 * l'échéancier. Tenue à part ici, l'accueil annonçait 43'129.36 d'impayés
 * quand le bilan en comptait 48'515.98.
 *
 * <p>L'Accueil, Factures et la Compta comptaient chacun à leur façon : trois
 * montants « impayés » différents sous les yeux du patron.
 *
 * @param {object} f facture
 * @param {object[]} [devis] offres, pour reconnaître l'acompte d'une offre
 * @param {object[]} [factures] toutes les factures, pour retrancher les avoirs
 * @returns {boolean} vrai si un solde reste à encaisser
 */
export function aEncaisser(f, devis, factures) {
  return (
    !!f &&
    f.type !== 'avoir' &&
    (f.statut === 'Envoyée' || f.statut === 'Partielle') &&
    !regleeParAvoir(f, factures)
  );
}

/**
 * Le total qui reste à encaisser sur les factures : le solde de chacune,
 * avoirs retranchés, non son montant entier.
 *
 * @param {object[]} factures factures de la société
 * @param {object[]} [devis] offres de la société
 * @returns {number} le total TTC à encaisser
 */
export function totalAEncaisser(factures, devis) {
  return (factures || [])
    .filter((f) => aEncaisser(f, devis, factures))
    .reduce((s, f) => s + soldeApresAvoirs(f, factures), 0);
}
